import request from 'superagent';
import { autobind } from 'core-decorators';
import get from 'lodash-es/get';
import has from 'lodash-es/has';
import fabric from '../../common/fabric';
import doUpload from '../upload/index';
import client from '../graphql/apollo';
import {
  prepareUploadMutation,
  createFileMutation,
} from '../graphql/mutations';
import utils from '../utils';
import { TechnicalError, SystemError, UserError } from '../lib/errors';
import { guessMimeType, dataURLtoBlob } from '../lib/files';
import {
  getCloudinaryURL,
  getImageURLKeyFromParams,
  getCachedVideoURL,
  getColorsFromImageOrContext,
  getLimitedDimensions,
} from '../images';
import { preloadUploadedImage } from './preloadActions';
import { UPLOAD_STATE_CANCELED } from '../upload/constants';
import smalltalk from '../../vendor/smalltalk';

const MIN_SVG_SIZE = 1500;

const teamId = window.team ? window.team.id : null;
const defaultErrorMessage =
  'Something went wrong during file upload. Please try again.';

/**
 * Get mime-type and "ludus-type" (for now, either image, video or lottie)
 *
 * @param file
 */
const getFileInfo = file =>
  new Promise((resolve, reject) => {
    const mimeType = guessMimeType(file);

    if (mimeType === 'application/json') {
      const reader = new FileReader();
      reader.addEventListener('loadend', e => {
        if (e.target.result) {
          let parsed;
          try {
            parsed = JSON.parse(e.target.result);
          } catch (err) {
            reject(
              new UserError(
                `"${file.name}" is not a valid Lottie file`,
                'Invalid file',
              ),
            );
          }
          const tests = ['v', 'w', 'h', 'fr'];
          if (tests.every(t => has(parsed, t))) {
            resolve({ type: 'lottie', mimeType });
          } else {
            reject(
              new UserError(
                `"${file.name}" is not a valid Lottie file`,
                'Invalid file',
              ),
            );
          }
        } else {
          reject(
            new UserError(`"${file.name}" could not be loaded`, 'Invalid file'),
          );
        }
      });
      reader.readAsText(file);
    } else {
      const resourceType = mimeType.split('/')[0];
      if (['image', 'video'].includes(resourceType)) {
        resolve({ type: resourceType, mimeType });
      } else {
        reject(
          new UserError(
            `Files of type "${mimeType}" are not supported`,
            'Invalid file',
          ),
        );
      }
    }
  });

const loadLocalFile = file => async () =>
  // eslint-disable-next-line no-async-promise-executor
  new Promise(async (resolve, reject) => {
    const { type } = await getFileInfo(file);
    if (type === 'image') {
      const imageFileURL = URL.createObjectURL(file);
      const image = new Image();
      image.onload = e => {
        const width = e.target.naturalWidth || e.target.width;
        const height = e.target.naturalHeight || e.target.height;
        const {
          width: limitedWidth,
          height: limitedHeight,
        } = getLimitedDimensions({ width, height });
        let imageURL;
        let colors;
        if (width > limitedWidth || height > limitedHeight) {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = limitedWidth;
          canvas.height = limitedHeight;
          ctx.drawImage(e.target, 0, 0, limitedWidth, limitedHeight);
          const imageDataURL = canvas.toDataURL('image/png');
          URL.revokeObjectURL(imageFileURL);
          imageURL = URL.createObjectURL(dataURLtoBlob(imageDataURL));
          colors = getColorsFromImageOrContext(null, ctx);
        } else {
          imageURL = imageFileURL;
          colors = getColorsFromImageOrContext(e.target, null);
        }
        resolve({
          url: imageURL,
          type,
          width,
          height,
          colors,
          size: file.size,
          local: true,
        });
      };
      image.onerror = () => {
        reject(
          new TechnicalError(`Cannot load local image at ${imageFileURL}`),
        );
      };
      image.src = imageFileURL;
    } else if (type === 'video') {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const videoFileURL = URL.createObjectURL(file);
      const video = document.createElement('video');

      video.addEventListener('loadeddata', e => {
        const width = e.target.videoWidth;
        const height = e.target.videoHeight;
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(e.target, 0, 0, width, height);
        const videoPosterDataURL = canvas.toDataURL('image/png');
        // 1x1 pixel base64-encoded PNG is around 50-100 bytes
        // If nothing is drawn, the length is 0.
        const success = videoPosterDataURL.length >= 50;
        if (success) {
          URL.revokeObjectURL(videoFileURL);
          const imageURL = URL.createObjectURL(
            dataURLtoBlob(videoPosterDataURL),
          );
          const colors = getColorsFromImageOrContext(null, ctx);
          resolve({
            url: imageURL,
            type,
            width,
            height,
            colors,
            size: file.size,
            local: true,
          });
        } else {
          reject(
            new TechnicalError(`Cannot load local video at ${videoFileURL}`),
          );
        }
      });

      video.addEventListener('error', () => {
        reject();
      });

      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;
      video.src = videoFileURL;
      video.play();
      //
    } else {
      resolve({ type });
    }
  });

/**
 * Ask for upload information from the backend.
 *
 * @param file
 */
const prepareUpload = file => async () => {
  const size = file.size;
  const { type, mimeType } = await getFileInfo(file);
  let result;
  try {
    result = await client.mutate({
      mutation: prepareUploadMutation,
      variables: { mimeType, size, teamId },
    });
  } catch (e) {
    throw new SystemError(defaultErrorMessage);
  }

  const { success, upload, validationErrors } = result.data.prepareUpload;

  if (success) {
    return {
      ...upload,
      mimeType,
      type,
    };
  }

  if (validationErrors.some(e => e.code === 'invalid' && e.field === 'size')) {
    const validationError = validationErrors.find(
      e => e.code === 'invalid' && e.field === 'size',
    );

    const limit = Math.round(
      parseInt(validationError.extra.limit, 10) / (1024 * 1024),
    );

    if (type === 'image') {
      throw new UserError(
        `The upload limit for images is ${limit}MB.`,
        'Upload limit',
      );
    } else if (type === 'video') {
      const message = `The upload limit for videos is ${limit}MB. You can use our YouTube or Vimeo integration to embed bigger files.`;
      throw new UserError(message, 'Upload limit');
    } else {
      throw new UserError(`The upload limit is ${limit}MB.`, 'Upload limit');
    }
  } else if (
    validationErrors.some(e => e.code === 'has_reached_upload_limit')
  ) {
    const validationError = validationErrors.find(
      e => e.code === 'has_reached_upload_limit',
    );
    const errorMessage =
      validationError.extra && validationError.extra.is_free
        ? 'You have reached the limit of the FREE plan. Upgrade to PRO if you want to continue uploading files.'
        : 'You have reached the limit of your plan. Please contact us.';

    throw new UserError(errorMessage, 'Upload limit');
  } else {
    throw new SystemError(defaultErrorMessage);
  }
};

const checkForSVGData = async file => {
  return new Promise(resolve => {
    if (file.type.includes('svg')) {
      try {
        const blob = URL.createObjectURL(file);
        fabric.util.request(blob, {
          method: 'get',
          onComplete: res => {
            const xml = res.responseXML;
            if (xml?.documentElement) {
              const hasSize =
                xml.documentElement.getAttribute('width') &&
                xml.documentElement.getAttribute('height');
              if (!hasSize) {
                let width = 300;
                let height = 300;
                const vbSize = utils.getSizeFromViewBox(
                  xml.documentElement.getAttribute('viewBox'),
                );
                if (vbSize) {
                  if (Math.max(vbSize.width, vbSize.height) > MIN_SVG_SIZE) {
                    width = vbSize.width;
                    height = vbSize.height;
                  } else {
                    if (vbSize.width >= vbSize.height) {
                      width = MIN_SVG_SIZE;
                      height = vbSize.height * (MIN_SVG_SIZE / vbSize.width);
                    } else {
                      height = MIN_SVG_SIZE;
                      width = vbSize.width * (MIN_SVG_SIZE / vbSize.height);
                    }
                  }
                }
                xml.documentElement.setAttribute('width', `${width}px`);
                xml.documentElement.setAttribute('height', `${height}px`);
              }
              const foreignObject = xml.documentElement.querySelector(
                'foreignObject',
              );
              if (foreignObject) {
                foreignObject.parentElement.removeChild(foreignObject);
              }
              const xmlsAsBlob = new Blob([xml.documentElement.outerHTML], {
                type: 'image/svg+xml',
              });
              resolve(xmlsAsBlob);
            }
          },
        });
      } catch (e) {
        console.info(e);
        resolve(file);
      }
    } else {
      resolve(file);
    }
  });
};

/**
 * Upload file to the provided upload URL.
 *
 * @param file: file path or Blob/Buffer object
 * @param uploadData
 */
const upload = (file, uploadData) => dispatch => {
  const progressCallback = progress => {
    dispatch({ type: 'PROGRESS_UPLOAD', progress });
  };

  return doUpload(file, uploadData, progressCallback);
};

/**
 * Create file record in our backend.
 *
 * @param uploadResult
 * @return {Promise.<*>}
 */
const createFile = uploadResult => async () => {
  const { mimeType, originalUrl, service, size } = uploadResult;
  let result;
  try {
    result = await client.mutate({
      mutation: createFileMutation,
      variables: {
        mimeType,
        remoteUrl: originalUrl,
        service,
        size,
        teamId,
      },
    });
  } catch (e) {
    throw new SystemError(defaultErrorMessage);
  }

  const { success, file } = result.data.createFile;

  if (success) {
    return {
      ...uploadResult,
      id: file.id,
    };
  }

  throw new SystemError(defaultErrorMessage);
};

const getMediaData = (uploadData, localData) => {
  const originalUrl = uploadData.fileUrl;
  const type = uploadData.type;
  const mimeType = uploadData.mimeType;
  const publicId = uploadData.uploadParams.public_id;
  const format = uploadData.fileFormat;
  const version = uploadData.uploadParams.timestamp;
  const size = localData.size;
  const originalWidth = localData.width;
  const originalHeight = localData.height;
  const originalDimensions = { width: originalWidth, height: originalHeight };
  const backgroundColor = get(localData, 'colors.main');
  const hasAlpha = get(localData, 'colors.hasAlpha');
  const textColor = get(localData, 'colors.contrast');
  const url = getCloudinaryURL(
    getImageURLKeyFromParams({
      publicId,
      format,
    }),
    { resource_type: type },
    originalDimensions,
  );

  const { width: limitedWidth, height: limitedHeight } = getLimitedDimensions(
    originalDimensions,
  );

  const data = {
    source: 'Cloudinary',
    type,
    mimeType,
    url,
    originalUrl,
    publicId,
    format,
    version,
    size,
    originalWidth,
    originalHeight,
    limitedWidth,
    limitedHeight,
    backgroundColor,
    hasAlpha,
    textColor,
  };

  if (type === 'video') {
    data.thumbnail = getCachedVideoURL({ publicId, format: 'jpg' });
  }

  return data;
};

let uploadQueue = null;

class UploadQueue {
  constructor(dispatch, getState) {
    this.dispatch = dispatch;
    this.getState = getState;
    this.running = false;
  }

  add(publicId, fileUrl, uploadData, replace) {
    // Inform the upload reducer that this upload is about to start
    this.dispatch({
      type: 'IMAGE_UPLOAD_PENDING',
      publicId,
      fileUrl,
      uploadData,
      replace,
    });
    this.run();
  }

  @autobind
  run() {
    const isOnline = this.getState().pingReducer;
    if (!isOnline) {
      window.clearTimeout(this.runTimeout);
      this.runTimeout = window.setTimeout(this.run, 5000);
      return;
    }

    if (this.running) {
      return;
    }

    const pendingUploads = this.getState().imagesReducer.pendingUploads;
    if (pendingUploads.length === 0) {
      return;
    }

    this.running = true;

    // Then, build a list of promise-returning functions that we will pass to `utils.promiseSerial`
    const uploadPromiseFactories = pendingUploads.map(uploadItem => () =>
      this.processUploadItem(uploadItem),
    );

    // Run upload promises serially
    utils
      .promiseSerial(uploadPromiseFactories)
      .then(() => {
        this.running = false;
        this.run();
      })
      .catch(error => {
        console.error(error);
        const failedImages = Object.entries(
          this.getState().imagesReducer.images,
        )
          .map(object => {
            return { id: object[0], ...object[1] };
          })
          .filter(image => image.uploadState === UPLOAD_STATE_CANCELED);
        if (failedImages.length) {
          window.failedImages = failedImages.map(img => img.url);
          this.dispatch({ type: 'FINAL_IMAGE_UPLOAD_ERROR', failedImages });
          smalltalk.alert(
            'Oops',
            'An error occured while uploading your file. Make sure your connection is up, and that you are not using an ad blocker. If the problem persists, please contact <a href="mailto:support@ludus.one">support@ludus.one</a>.',
          );
        }
        this.running = false;
        window.clearTimeout(this.runTimeout);
        this.runTimeout = window.setTimeout(this.run, 2000);
      });
  }

  processUploadItem(uploadItem) {
    return new Promise((resolve, reject) => {
      const { publicId, pendingUpload } = uploadItem;
      const { uploadData, fileUrl } = pendingUpload;

      // Retrieve a Blob (File) object from its local URL (blob:http://....)
      request
        .get(fileUrl)
        .responseType('blob')
        .end((err, response) => {
          if (err) {
            reject(err);
          }
          const file = response.body;
          this.dispatch({ type: 'IMAGE_UPLOAD_START', publicId });
          doUpload(file, uploadData, progress => {
            this.dispatch({
              type: 'IMAGE_UPLOAD_PROGRESS',
              progress,
              publicId,
            });
          })
            .then(uploadResult => this.dispatch(createFile(uploadResult)))
            .then(() => {
              this.dispatch({ type: 'IMAGE_UPLOAD_COMPLETE', publicId });
              this.dispatch(preloadUploadedImage(publicId));
              URL.revokeObjectURL(fileUrl);
              resolve();
            })
            .catch(error => {
              this.dispatch({ type: 'IMAGE_UPLOAD_ERROR', publicId });
              reject(error);
            });
        });
    });
  }
}

export const enqueueUpload = (publicId, fileUrl, uploadData, replace) => (
  dispatch,
  getState,
) => {
  if (uploadQueue === null) {
    uploadQueue = new UploadQueue(dispatch, getState);
  }
  uploadQueue.add(publicId, fileUrl, uploadData, replace);
};

export const uploadMediaFile = (file, replace) => dispatch =>
  new Promise((resolve, reject) => {
    dispatch(prepareUpload(file))
      .then(uploadData => {
        dispatch(loadLocalFile(file))
          .then(localData => {
            const publicId = uploadData.uploadParams.public_id;
            const fileUrl = URL.createObjectURL(file);
            dispatch({
              type: 'LOCAL_IMAGE_LOAD_SUCCESS',
              publicId,
              url: localData.url,
            });
            resolve(getMediaData(uploadData, localData));
            dispatch(enqueueUpload(publicId, fileUrl, uploadData, replace));
          })
          .catch(error => {
            if (error instanceof TechnicalError) {
              // Local file couldn't be loaded, let's try to upload it first to Cloudinary
              // so they handle the dirty work
              dispatch(uploadFile(file))
                .then(resolve)
                .catch(reject);
            } else {
              reject(error);
            }
          });
      })
      .catch(error => {
        reject(error);
      });
  });

export const uploadFile = (
  originalFile,
  save = true,
  instant = false,
  replace,
) => async dispatch => {
  const file = await checkForSVGData(originalFile);
  if (instant) {
    let info;
    try {
      info = await getFileInfo(file);
    } catch (error) {
      return new Promise((resolve, reject) => reject(error));
    }
    if (['image', 'video'].includes(info.type)) {
      return dispatch(uploadMediaFile(file, replace));
    }
  }

  return new Promise((resolve, reject) => {
    dispatch({ type: 'START_UPLOAD' });

    const savePromise = save
      ? uploadResult => dispatch(createFile(uploadResult))
      : uploadResult => Promise.resolve(uploadResult);

    dispatch(prepareUpload(file))
      .then(uploadData => dispatch(upload(file, uploadData)))
      .then(savePromise)
      .then(finalResult => {
        dispatch({ type: 'PROGRESS_UPLOAD', progress: 1 });
        window.setTimeout(() => {
          dispatch({ type: 'END_UPLOAD' });
        }, 500);
        resolve(finalResult);
      })
      .catch(error => {
        dispatch({ type: 'END_UPLOAD' });
        reject(error);
      });
  });
};
